import type {
  CogoXyzEvidenceStatus,
  CogoXyzEvidenceType,
  CogoXyzInspectPreviewInput,
} from '@cogo/types-v2'

export interface CogoXyzPreviewProofArtifact {
  type: Extract<CogoXyzEvidenceType, 'browser_check' | 'console_log' | 'screenshot'>
  artifactRef: string
  summary: string
  status: CogoXyzEvidenceStatus
  metadata?: Record<string, unknown>
}

export interface CogoXyzPreviewProofResult {
  status: 'passed' | 'failed' | 'partial'
  summary: string
  artifacts: CogoXyzPreviewProofArtifact[]
  metadata?: Record<string, unknown>
}

function proofArtifactRef(kind: string): string {
  const random = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
  return `cogo-xyz://preview-proof/${kind}/${random}`
}

export async function runCogoXyzPreviewProof(
  input: CogoXyzInspectPreviewInput,
): Promise<CogoXyzPreviewProofResult> {
  const artifacts: CogoXyzPreviewProofArtifact[] = [{
    type: 'browser_check',
    artifactRef: proofArtifactRef('browser-check'),
    summary: 'Browser proof runner is not connected in this runtime.',
    status: 'created',
    metadata: {
      previewUrl: input.previewUrl,
      checks: input.checks,
      proofLevel: 'runner-unavailable',
    },
  }]

  if (input.checks.includes('console')) {
    artifacts.push({
      type: 'console_log',
      artifactRef: proofArtifactRef('console-log'),
      summary: 'Console capture requires a connected browser proof runner.',
      status: 'created',
      metadata: { previewUrl: input.previewUrl },
    })
  }

  if (input.checks.includes('screenshot')) {
    artifacts.push({
      type: 'screenshot',
      artifactRef: proofArtifactRef('screenshot'),
      summary: 'Screenshot capture requires a connected browser proof runner.',
      status: 'created',
      metadata: { previewUrl: input.previewUrl },
    })
  }

  return {
    status: 'partial',
    summary: 'Inspection evidence was recorded; browser proof runner is pending.',
    artifacts,
    metadata: {
      runner: 'unavailable',
      requestedChecks: input.checks,
    },
  }
}
